import * as React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import Joyride, {
  ACTIONS,
  CallBackProps,
  EVENTS,
  STATUS,
  Step,
} from "react-joyride";
import { useHistory } from "react-router-dom";
import ProductTourIntro from "./ProductTourIntro";
import ProductTourTooltip from "./ProductTourTooltip";
import { getTourDefinition } from "./tourRegistry";
import { isTourDone as readTourDone, markTourDone } from "./tourPersistence";
import { filterStepsByDom } from "./tourSteps";
import type {
  EwTourStep,
  ProductTourContextValue,
  ProductTourId,
  TourDefinition,
  TourIntroDef,
  TourSegment,
} from "./types";
import "./product-tour.scss";

const ProductTourContext = createContext<ProductTourContextValue | null>(null);

function wait(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

function activateSelector(selector?: string) {
  if (!selector) return;
  const el = document.querySelector(selector) as HTMLElement | null;
  if (!el) return;
  el.scrollIntoView({ block: "center", behavior: "smooth" });
  const expanded = el.getAttribute("aria-expanded");
  if (expanded === "true") return;
  if (typeof el.click === "function") {
    el.click();
  }
}

function withProgressMeta(
  steps: EwTourStep[],
  progressOffset: number,
  progressTotal: number
): EwTourStep[] {
  return steps.map((step, index) => ({
    ...step,
    data: {
      ...step.data,
      progressIndex: progressOffset + index,
      progressTotal,
    },
  }));
}

function filterAvailableSteps(steps: EwTourStep[]): Step[] {
  return filterStepsByDom(steps, (sel) =>
    Boolean(document.querySelector(sel))
  ).map((step) => ({
    ...step,
    content: step.data.bodyKey,
    title: step.data.titleKey,
  }));
}

async function waitForSelector(
  selector: string,
  retries = 12,
  delayMs = 250
): Promise<boolean> {
  for (let i = 0; i < retries; i += 1) {
    if (document.querySelector(selector)) return true;
    await wait(delayMs);
  }
  return false;
}

async function resolveSegmentRoute(
  segment: TourSegment,
  history: ReturnType<typeof useHistory>
): Promise<void> {
  const current = `${history.location.pathname}${history.location.search}`;
  let targetRoute = segment.route;
  if (segment.resolveRoute) {
    const resolved = await segment.resolveRoute();
    if (resolved) {
      targetRoute = resolved;
    }
  }

  if (!current.startsWith(targetRoute.split("?")[0])) {
    history.push(targetRoute);
    await wait(700);
  }
}

export function ProductTourProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const history = useHistory();
  const [introOpen, setIntroOpen] = useState(false);
  const [intro, setIntro] = useState<TourIntroDef | null>(null);
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState<Step[]>([]);
  const [totalSteps, setTotalSteps] = useState(0);
  const activeTourIdRef = useRef<ProductTourId | null>(null);
  const pendingDefRef = useRef<TourDefinition | null>(null);
  const activeDefRef = useRef<TourDefinition | null>(null);
  const segmentIndexRef = useRef(0);

  const finishTour = useCallback(() => {
    const id = activeTourIdRef.current;
    if (id) markTourDone(id);
    setRun(false);
    setSteps([]);
    setStepIndex(0);
    setTotalSteps(0);
    setIntroOpen(false);
    setIntro(null);
    pendingDefRef.current = null;
    activeDefRef.current = null;
    segmentIndexRef.current = 0;
    activeTourIdRef.current = null;
  }, []);

  const beginSegment = useCallback(async (
    def: TourDefinition,
    segmentIndex: number
  ) => {
    const segment = def.segments[segmentIndex];
    if (!segment) {
      finishTour();
      return;
    }

    await resolveSegmentRoute(segment, history);
    const firstTarget = segment.steps[0]?.target;
    if (typeof firstTarget === "string" && firstTarget !== "body") {
      await waitForSelector(firstTarget);
    }
    const progressOffset = def.segments
      .slice(0, segmentIndex)
      .reduce((sum, item) => sum + item.steps.length, 0);
    const rawSteps = withProgressMeta(
      segment.steps,
      progressOffset,
      totalSteps || def.segments.reduce((sum, item) => sum + item.steps.length, 0)
    );
    const available = filterAvailableSteps(rawSteps);
    if (!available.length) {
      if (segmentIndex + 1 < def.segments.length) {
        segmentIndexRef.current = segmentIndex + 1;
        await beginSegment(def, segmentIndex + 1);
        return;
      }
      finishTour();
      return;
    }

    segmentIndexRef.current = segmentIndex;
    setRun(false);
    setSteps(available);
    setStepIndex(0);
    const first = available[0] as Step & {
      data?: { activateSelector?: string };
    };
    activateSelector(first?.data?.activateSelector);
    await wait(200);
    setRun(true);
  }, [finishTour, history, totalSteps]);

  const startTour = useCallback(
    (tourId: ProductTourId) => {
      const def = getTourDefinition(tourId);
      activeTourIdRef.current = tourId;
      pendingDefRef.current = def;
      activeDefRef.current = def;
      setIntro(null);
      setIntroOpen(false);
      setRun(false);
      setSteps([]);
      setStepIndex(0);
      setTotalSteps(def.segments.reduce((sum, segment) => sum + segment.steps.length, 0));

      const firstSegment = def.segments[0];
      if (!firstSegment) return;
      void resolveSegmentRoute(firstSegment, history).then(() => {
        if (activeTourIdRef.current !== tourId) return;
        setIntro(def.intro);
        setIntroOpen(true);
      });
    },
    [history]
  );

  const onIntroContinue = useCallback(() => {
    const def = pendingDefRef.current;
    setIntroOpen(false);
    if (!def) return;
    void beginSegment(def, 0);
  }, [beginSegment]);

  const onIntroSkip = useCallback(() => {
    const def = pendingDefRef.current;
    setIntroOpen(false);
    setIntro(null);
    if (def) {
      markTourDone(def.id);
      const firstSegment = def.segments[0];
      if (firstSegment) {
        void resolveSegmentRoute(firstSegment, history);
      }
    }
    activeTourIdRef.current = null;
    pendingDefRef.current = null;
  }, [history]);

  const handleJoyride = useCallback(
    (data: CallBackProps) => {
      const { status, type, index, action, step } = data;
      const finished =
        status === STATUS.FINISHED || status === STATUS.SKIPPED;

      if (type === EVENTS.STEP_BEFORE) {
        const dataStep = step as Step & {
          data?: { activateSelector?: string };
        };
        activateSelector(dataStep?.data?.activateSelector);
      }

      if (finished) {
        finishTour();
        return;
      }

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        const next = index + (action === ACTIONS.PREV ? -1 : 1);
        if (next >= steps.length) {
          const def = activeDefRef.current;
          const nextSegmentIndex = segmentIndexRef.current + 1;
          if (def && nextSegmentIndex < def.segments.length) {
            void beginSegment(def, nextSegmentIndex);
            return;
          }
          finishTour();
          return;
        }
        if (next >= 0) {
          setStepIndex(next);
        }
      }
    },
    [beginSegment, finishTour, steps]
  );

  const value = useMemo<ProductTourContextValue>(
    () => ({
      startTour,
      isTourDone: readTourDone,
    }),
    [startTour]
  );

  return (
    <ProductTourContext.Provider value={value}>
      {children}
      <ProductTourIntro
        open={introOpen}
        intro={intro}
        onContinue={onIntroContinue}
        onSkip={onIntroSkip}
      />
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        scrollToFirstStep
        showSkipButton
        disableOverlayClose
        spotlightClicks={false}
        callback={handleJoyride}
        tooltipComponent={ProductTourTooltip}
        floaterProps={{ disableAnimation: true }}
        styles={{
          options: {
            zIndex: 10050,
            arrowColor: "#fff",
            overlayColor: "rgba(15, 23, 42, 0.45)",
          },
        }}
      />
    </ProductTourContext.Provider>
  );
}

export function useProductTour(): ProductTourContextValue {
  const ctx = useContext(ProductTourContext);
  if (!ctx) {
    throw new Error("useProductTour must be used within ProductTourProvider");
  }
  return ctx;
}
